class LifestyleHotspots extends HTMLElement {
  connectedCallback () {
    this.cleanups = []
    this.desktopMedia = window.matchMedia('(min-width: 768px)')

    const pairs = [...this.querySelectorAll('button[popovertarget].lifestyle-carousel__hotspot-dot')]
      .map(button => ({
        button,
        popover: this.querySelector(`#${CSS.escape(button.getAttribute('popovertarget'))}`),
      }))
      .filter(pair => pair.popover)

    pairs.forEach(pair => {
      this.setupScrollClose(pair)
      this.setupPositioning(pair)
      this.setupActiveState(pair)
      this.setupMobileModal(pair)
    })
  }

  disconnectedCallback () {
    this.cleanups.forEach(cleanup => cleanup())
    this.cleanups = []
  }

  setActive (button) {
    this.classList.add('lifestyle-carousel__hotspots--open')
    button.classList.add('lifestyle-carousel__hotspot-dot--active')
  }

  clearActive () {
    this.classList.remove('lifestyle-carousel__hotspots--open')
    this.querySelectorAll('.lifestyle-carousel__hotspot-dot--active')
      .forEach(dot => dot.classList.remove('lifestyle-carousel__hotspot-dot--active'))
  }

  setupActiveState ({ button, popover }) {
    const handleToggle = (e) => {
      if (e.newState === 'open') {
        this.setActive(button)
      } else {
        this.clearActive()
      }
    }

    popover.addEventListener('toggle', handleToggle)

    this.cleanups.push(() => {
      popover.removeEventListener('toggle', handleToggle)
    })
  }

  setupMobileModal ({ button, popover }) {
    const handleClick = (event) => {
      if (this.desktopMedia.matches) return
      event.preventDefault()
      this.openModal(button, popover)
    }

    button.addEventListener('click', handleClick)

    this.cleanups.push(() => {
      button.removeEventListener('click', handleClick)
    })
  }

  openModal (button, popover) {
    const modal = document.querySelector('reusable-modal')
    const modalBody = modal?.querySelector('.reusable-modal__body')
    const container = popover.querySelector('.lifestyle-carousel__hotspot-container')
    if (!modalBody || !container) return

    const closeEvent = 'lifestyle-hotspots:modal-closed'

    window.dispatchEvent(new CustomEvent('open:modal', {
      detail: {
        width: 'min(500px, 100svw)',
        padding: 'none',
        headerClassAdd: ['absolute', 'h-15', 'top-0', 'left-0', 'right-0', 'z-20'],
        headerClassRemove: ['border-b', 'grid', 'mb-4', 'pb-2'],
        bodyClassAdd: ['relative', 'z-10', 'p-4', 'pt-14'],
        closeClassAdd: ['absolute', 'top-4', 'right-4', 'z-20'],
        onCloseEvent: closeEvent,
      },
    }))

    const modalTitle = modal.querySelector('.reusable-modal__title')
    if (modalTitle) modalTitle.textContent = ''

    const type = popover.dataset.type || '1'
    modalBody.innerHTML = `<div class="lifestyle-carousel__hotspot-modal lifestyle-carousel__hotspot-modal--type-${type} text-black">${container.outerHTML}</div>`

    this.setActive(button)
    window.addEventListener(closeEvent, () => this.clearActive(), { once: true })
  }

  setupScrollClose ({ popover }) {
    let startY = 0

    const handleScroll = () => {
      if (Math.abs(window.scrollY - startY) > 48 && popover.matches(':popover-open')) {
        popover.hidePopover()
      }
    }

    const handleToggle = (e) => {
      if (e.newState === 'open') {
        startY = window.scrollY
        window.addEventListener('scroll', handleScroll, { passive: true })
      } else {
        window.removeEventListener('scroll', handleScroll)
      }
    }

    popover.addEventListener('toggle', handleToggle)

    this.cleanups.push(() => {
      popover.removeEventListener('toggle', handleToggle)
      window.removeEventListener('scroll', handleScroll)
    })
  }

  setupPositioning ({ button, popover }) {
    const positionPopover = () => this.positionPopover(button, popover)

    const handleReposition = () => {
      if (popover.matches(':popover-open')) {
        positionPopover()
      }
    }

    const handleToggle = (e) => {
      if (e.newState === 'open') {
        requestAnimationFrame(positionPopover)
        window.addEventListener('scroll', handleReposition, true)
        window.addEventListener('resize', handleReposition)
      } else {
        window.removeEventListener('scroll', handleReposition, true)
        window.removeEventListener('resize', handleReposition)
      }
    }

    popover.addEventListener('toggle', handleToggle)

    this.cleanups.push(() => {
      popover.removeEventListener('toggle', handleToggle)
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    })
  }

  positionPopover (button, popover) {
    const gap = 13
    const margin = 16
    const buttonRect = button.getBoundingClientRect()
    const popoverRect = popover.getBoundingClientRect()
    const buttonCenterX = buttonRect.left + buttonRect.width / 2
    const buttonCenterY = buttonRect.top + buttonRect.height / 2

    let placement = 'right'
    let left = buttonRect.right + gap

    if (left + popoverRect.width > window.innerWidth - margin) {
      placement = 'left'
      left = buttonRect.left - popoverRect.width - gap
    }

    if (placement === 'left' && left < margin) {
      placement = 'below'
    }

    let top

    if (placement === 'below') {
      left = buttonRect.left
      if (left + popoverRect.width > window.innerWidth - margin) {
        left = buttonRect.right - popoverRect.width
      }
      left = Math.max(margin, left)

      top = buttonRect.bottom + gap
      if (top + popoverRect.height > window.innerHeight - margin) {
        top = buttonRect.top - popoverRect.height - gap
        placement = 'above'
      }
      top = Math.max(margin, top)

      const caretX = Math.min(Math.max(buttonCenterX - left, 22), popoverRect.width - 22)
      popover.style.setProperty('--caret-x', `${caretX}px`)
    } else {
      top = buttonCenterY - popoverRect.height / 2
      top = Math.min(top, window.innerHeight - margin - popoverRect.height)
      top = Math.max(margin, top)

      const caretY = Math.min(Math.max(buttonCenterY - top, 22), popoverRect.height - 22)
      popover.style.setProperty('--caret-y', `${caretY}px`)
    }

    popover.style.left = `${left}px`
    popover.style.top = `${top}px`
    popover.dataset.placement = placement
  }
}

if (!customElements.get('lifestyle-hotspots')) {
  customElements.define('lifestyle-hotspots', LifestyleHotspots)
}
